import React, { useEffect, useCallback, forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import Help from '../Help/Help.jsx'
import { useSelector, useDispatch } from 'react-redux'
import Loading from '../Loading/Loading.jsx'
import { updateWindow } from 'actions/interactableActions'
import FavoriteButton from 'GeneralUI/FavoriteButton/FavoriteButton.jsx'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const LoadingWindow = forwardRef((props, ref) => {
  const nullFunction = useCallback(() => {
    return null
  }, [])

  const container = useSelector(state => state.interactableState.container)
  const dispatch = useDispatch()

  const [prevSize, setPrevSize] = useState(null)

  const {
    className = null,
    title = null,
    helpId = false,
    closeable = true,
    draggable = true,
    resizeable = true,
    canFavorite = true,
    zIndexHandler = nullFunction,
    closeCallback,
    closeHandler,
    windowConfig
  } = props

  const { asModal = false } = windowConfig

  const closeWrapper = useCallback(() => {
    // note, this isn't checking for the existence of a function... the close callback is called before
    // the window is closed. It does its work and only if it returns true will the window actually be
    // closed by the close handler.
    if (closeCallback()) {
      closeHandler()
    }
  }, [closeCallback, closeHandler])

  useEffect(() => {
    // componentDidMount
    zIndexHandler()
  }, [])

  const adjustWindowSize = useCallback(e => {
    // when maximizing prevSize stores the original size so we know what to restore to.
    // if its null that means we are at the "normal" size and are doing mazimize
    const { x, y, width, height, ...rest } = windowConfig
    if (!prevSize) {
      const { width: containerWidth, height: containerHeight } = container
      dispatch(updateWindow({
        width: containerWidth,
        height: containerHeight,
        x: 0,
        y: 0,
        ...rest
      }))
      setPrevSize({ x, y, width, height })
    } else {
      dispatch(updateWindow({ ...prevSize, ...rest }))
      setPrevSize(null)
    }
  }, [
    container.width,
    container.height,
    windowConfig.x,
    windowConfig.y,
    windowConfig.width,
    windowConfig.height,
    prevSize
  ])
  const adjustSizeIcon = prevSize == null ? 'window-maximize' : 'window-minimize'
  const headerStyle = { backgroundColor: '#8373ba', borderColor: '#8373ba' }
  const footerStyle = {
    backgroundColor: '#8373ba',
    borderColor: '#8373ba'
  }
  return (
    <div
      className={'draggable resizeable window ' + className}
      ref={ref}
      onMouseDown={zIndexHandler}
      data-windowname={windowConfig.ref}
    >
      <div className="list-component list-group window-window">
        <div
          style={headerStyle}
          className={`list-group-item active header'
          }`}
          aria-label={`${title} window header`}
        >
          <div
            className={'header' + (draggable || asModal ? ' draggable-handle' : '')}
            aria-label={`${title} window header`}
          >
            <div className="corner-icons">
              {draggable || asModal
                ? <FontAwesomeIcon
                    icon={['fas', 'bars']}
                    className="draggable-handle"
                    aria-label={`${title} window drag icon`}
                  />
                : <div />
              }
            </div>
            <div className="list-group-title">
              <span className='title'>
                {title}
              </span>
              {helpId
                ? <Help id={helpId} aria-label={`${title} window help icon`} />
                : null
              }
            </div>
            <div className="corner-icons">
              <div />
              <FavoriteButton name={windowConfig.ref} type='window' canFavorite={canFavorite}/>
              {
                resizeable
                  ? <div className="adjust-window-size" onClick={adjustWindowSize}>
                    <FontAwesomeIcon
                      icon={['far', adjustSizeIcon]}
                      aria-label={`${adjustSizeIcon} window icon`}
                      className="size-button"
                    />
                  </div>
                  : <div/>
              }
              {closeable || asModal
                ? <div className="closeWindow-div" onClick={closeWrapper}>
                    <FontAwesomeIcon
                      icon={['fas', 'times']}
                      aria-label={`${title} window close icon`}
                      className="close-button"
                    />
                  </div>
                : <div />
              }
            </div>
          </div>
        </div>
        <div className="window-content">
          <div> this is it!</div>
          <Loading/>
        </div>
        <div
          style={footerStyle}
          className={'list-group-item active resize-footer'}
        >
          {resizeable || asModal
            ? <FontAwesomeIcon
                icon={['far', 'arrows-alt-h']}
                transform={{ rotate: 45 }}
                className="resizeable-handle"
                aria-label={`${title} window resize icon`}
                onClick={() => setPrevSize(null) /* bring back maximize button */}
              />
            : null
          }
        </div>
      </div>
    </div>
  )
})

LoadingWindow.propTypes = {
  // //////////////////////////////////////////////////////////////////////
  // Note windows must be run in the parent WindowContainer component
  // //////////////////////////////////////////////////////////////////////

  // classname to apply to outermost window div
  className: PropTypes.string,
  // Text to display in window header (overrides window config if present)
  title: PropTypes.string,
  // Bool to determine if loading spinner should be rendered instead of children
  loading: PropTypes.bool,
  // Config details to be applied to the window
  // This is automatically passed in by WindowContainer
  windowConfig: PropTypes.shape({
    // Width to display window in px (after conversion from percent by windowContainer)
    width: PropTypes.number,
    // Height to display window in px (after conversion from percent by windowContainer)
    height: PropTypes.number,
    // X position of the upper left window corner in px (after conversion from percent by windowContainer)
    x: PropTypes.number,
    // Y position of the upper left window corner  in px (after conversion from percent by windowContainer)
    y: PropTypes.number,
    // User visible name of the window
    name: PropTypes.string,
    // the ref attached to the window component within the WindowContainer component
    ref: PropTypes.string.isRequired,
    // Should the window be visible in the window menu
    selectable: PropTypes.bool
  }),
  // If present, questionMark will be displayed in the upper right corner and will
  // display text associated with the helpId string in the data/help.js file
  helpId: PropTypes.string,
  // should the window have a close X in the upper right
  closeable: PropTypes.bool,
  // Should the window be draggable and have drag handle in upper left
  draggable: PropTypes.bool,
  // Should the window be resizable and have arrow handle at lower right
  resizeable: PropTypes.bool,
  // function to handle managing the zIndex of multiple windows open at any time
  // this is automatically applied by WindowContainer
  zIndexHandler: PropTypes.func.isRequired,
  // function to attach to the close X in the upper right corner
  // this is automatically applied by WindowContainer
  closeHandler: PropTypes.func.isRequired
}
export default LoadingWindow
